/**
 * How a pipeline stage looks: the icons, colours and probabilities offered when one is edited.
 *
 * Shared because two screens now offer the same choices — the stage popup (add one stage, or edit
 * one) and manage mode (edit them all in the board's lanes). Two copies would drift, and a stage
 * created with an icon the other screen does not list is a stage nobody can edit back.
 */

// The app's own icon classes, not the prototype's Font Awesome codepoints. Every one of these is
// checked against src/assets/css/wequote-icons.css — wq-trophy, wq-settings and wq-box are not in
// the font, whatever the design draws.
export const CRM_STAGE_ICONS = [
	{ label: 'No icon', icon: '' },
	{ label: 'Checked', icon: 'wq wq-checkbox-checked' },
	{ label: 'Quote', icon: 'wq wq-document-edit' },
	{ label: 'Email', icon: 'wq wq-email' },
	{ label: 'Edit', icon: 'wq wq-edit' },
	{ label: 'Handshake', icon: 'wq wq-handshake' },
	{ label: 'Target', icon: 'wq wq-target' },
	{ label: 'Flag', icon: 'wq wq-flag' },
	{ label: 'Check', icon: 'wq wq-check' },
	{ label: 'Close', icon: 'wq wq-close' },
	{ label: 'Clock', icon: 'wq wq-clock' },
	{ label: 'Archive', icon: 'wq wq-list' }
];

export const CRM_STAGE_COLOURS = ['#576A92', '#7C3AED', '#B97A00', '#1E8539', '#F12B53'];

export const CRM_STAGE_PROBABILITIES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/**
 * The segments that sit inside an outcome, and the outcome each one carries.
 *
 * Mirrors CRM::OUTCOME_SEGMENTS. A stage placed here is after Won but not after the deal: the
 * quote is still accepted and the deal is still won, and the lane says where the work has got to
 * since (D-097). Won only — nothing happens to a deal after it is lost.
 */
export const CRM_OUTCOME_SEGMENTS = { Won: 'won', Invoicing: 'won' };

/**
 * The milestone a deal is marked won into, and the one after it that follows the invoices.
 *
 * Mirror CRM::WON_STAGE and CRM::INVOICING_STAGE. Both carry `outcome = 'won'`, so "the Won stage"
 * is found by name: Invoicing is never a drop target or a win button — the deal moves there by
 * itself when an invoice for the accepted quote is issued.
 */
export const CRM_WON_STAGE = 'Won';
export const CRM_INVOICING_STAGE = 'Invoicing';

// Whether this is the stage a deal is marked won into (not Invoicing, which also carries the outcome).
export const crmIsWonMilestone = (stage: any): boolean =>
	!!stage && stage.outcome === 'won' && (!stage.protected || stage.description === CRM_WON_STAGE);

/**
 * The outcome a stage puts a deal in: its own, or the one its segment carries.
 *
 * Ask this, not `stage.outcome`, wherever the question is about the deal — is it won, is it
 * closed, may it start here. `outcome` is set on the two protected milestones only, so a lane
 * after Won reads as open to anything checking the column directly.
 */
export const crmStageOutcome = (stage: any): string => {
	if (!stage) return null;
	if (stage.outcome) return stage.outcome;
	if (stage.protected) return null;

	return CRM_OUTCOME_SEGMENTS[stage.lifecycle_segment] || null;
};

// Whether a deal standing here is still in play. The lanes after Won and Lost are not.
export const crmStageOpen = (stage: any): boolean => !crmStageOutcome(stage);

// The first segment of the quote lifecycle: "no quote yet". Mirrors CRM::QUOTE_SEGMENTS[0].
export const CRM_FIRST_SEGMENT = 'Qualified';

/**
 * Whether a new deal may be created straight into this stage of a quote-connected pipeline.
 *
 * Mirrors CRM::can_enter_stage(), which the endpoint enforces. A new deal has no quotes, so it
 * derives to the first segment — the Qualified milestone, and any stage the organisation added
 * inside that segment, because a deal in a custom stage stays there while its segment matches the
 * derived stage (CRM-136).
 *
 * A lane in a later segment cannot hold a new deal: nothing there is true yet, and the first quote
 * event would pull it back to Qualified.
 *
 * A NULL segment reads as the first one, as the backend reads it.
 */
export const crmStageAcceptsNewDeal = (stage: any): boolean => {
	if (!stage) return false;
	if (stage.protected) return stage.description === CRM_FIRST_SEGMENT;

	return (stage.lifecycle_segment || CRM_FIRST_SEGMENT) === CRM_FIRST_SEGMENT;
};

// Won is 100% and Lost is 0% by definition, and Sales Overview reads those numbers — so the picker
// is disabled rather than merely ignored on save.
export const crmStageProbabilityLocked = (stage: any): boolean => !!stage?.outcome;

export const crmStageProbabilityHelp = (stage: any): string => {
	if (stage?.outcome === 'won') return stage.description + ' is fixed at 100%.';
	if (stage?.outcome === 'lost') return 'Lost is fixed at 0%.';
	return 'Won is 100%; Lost is 0%.';
};

/**
 * What each kind of pipeline opens with, for the Create pipeline preview only (D-068).
 *
 * The database is the authority — CRM::QUOTE_STAGES and CRM::STANDALONE_STAGES lay the real rows
 * down — so this is a picture of a pipeline that does not exist yet, kept in step by name.
 */
export const QUOTE_STAGE_PREVIEW = [
	{ description: 'Qualified', icon: 'wq wq-checkbox-checked', color: '#576A92', probability: 10, protected: 1 },
	{ description: 'In Progress', icon: 'wq wq-document-edit', color: '#2450FF', probability: 30, protected: 1 },
	{ description: 'In Review', icon: 'wq wq-search', color: '#7C3AED', probability: 45, protected: 1 },
	{ description: 'Passed Review', icon: 'wq wq-check', color: '#8B5CF6', probability: 60, protected: 1 },
	{ description: 'Sent', icon: 'wq wq-send', color: '#B97A00', probability: 75, protected: 1 },
	{ description: 'Won', icon: 'wq wq-handshake', color: '#1E8539', probability: 100, protected: 1 },
	{ description: 'Invoicing', icon: 'wq wq-card', color: '#178F77', probability: 100, protected: 1 },
	{ description: 'Lost', icon: 'wq wq-close', color: '#8294BA', probability: 0, protected: 1 }
];

export const CRM_QUOTE_STAGE_LABELS = {
	cancelled: 'Rejected',
	in_progress: 'In progress',
	in_review: 'In review',
	passed_review: 'Passed review',
	sent: 'Sent',
	accepted: 'Accepted',
	complete: 'Complete'
};

export const STANDALONE_STAGE_PREVIEW = [
	{ description: 'New', icon: 'wq wq-checkbox-checked', color: '#576A92', probability: 10, protected: 0 },
	{ description: 'In Progress', icon: 'wq wq-edit', color: '#2450FF', probability: 45, protected: 0 },
	{ description: 'Complete', icon: 'wq wq-check', color: '#1E8539', probability: 90, protected: 0 },
	{ description: 'Won', icon: 'wq wq-handshake', color: '#1E8539', probability: 100, protected: 1 },
	{ description: 'Lost', icon: 'wq wq-close', color: '#8294BA', probability: 0, protected: 1 }
];
